import type { SectionRequest } from '@breakoutlearning/firebase-repository/models/SectionRequest'
import { formatCurrency } from '@breakoutlearning/firebase-repository/util'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { ConfirmationDialog } from 'components/dialogs/ConfirmationDialog'
import { CircleX } from 'components/icons/CircleX'
import { ClockIcon } from 'components/icons/Clock'
import { FullPageSpinner } from 'components/Spinner'
import { useRepository } from 'hooks/auth'
import { useInstructorSectionCubit } from 'hooks/cubits/instructorSection'
import { useDialogs } from 'hooks/dialogs'
import { useBreakoutUser } from 'hooks/profile'
import { useInvoiceRequestEstimatedCost } from 'hooks/useInvoiceRequestEstimatedCost'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'

export const PendingRequestState = observer(function PendingRequestState({
  sectionRequest,
  setToStudentPay,
  createNewRequest,
}: {
  sectionRequest: SectionRequest
  setToStudentPay: () => Promise<void>
  createNewRequest: () => void
}) {
  const { t, tt } = useTranslationTyped()
  const sectionCubit = useInstructorSectionCubit()
  const cubit = useInstructorSectionCubit()
  const user = useBreakoutUser()
  const sectionProduct = cubit.sectionProduct
  const { showDialog } = useDialogs()
  const repository = useRepository()

  const { estimatedCost, isProductPriced, sectionPassPriceCents, productName } =
    useInvoiceRequestEstimatedCost(
      sectionRequest,
      cubit.organizationCustomProducts.models,
      sectionProduct.value?.data.productId,
      cubit.section.data.sectionPassPrice
    )

  const productPriceCents = sectionPassPriceCents ?? 0

  if (
    sectionProduct.isLoading ||
    cubit.organizationCustomProducts.isLoading ||
    user.defaultSystemProducts.isLoading
  ) {
    return <FullPageSpinner reason="lti_pending_request" />
  }

  const rejectedByUserId = sectionRequest.data.updatedByUserId
  const rejectedBy = rejectedByUserId
    ? repository.userStore.getUser(rejectedByUserId)
    : null

  return (
    <div className="invoicing-submitted flex min-h-full w-full flex-col items-start justify-center">
      <div className="ml-[5vw] max-w-[450px]">
        <div className="mb-5">
          <h1 className="text-headline-large">
            {t('lti.configuration.invoice_request_pending')}
          </h1>
          <div className="text-body-large">
            {sectionRequest.isPending &&
              t('lti.configuration.invoice_request_pending_description')}
            {sectionRequest.isRejected &&
              t('lti.configuration.invoice_request_rejected_description')}
          </div>
        </div>
        <div className="rounded-xl bg-core-secondary p-4">
          <div className="mt-1 mb-5 flex flex-row items-center gap-1 text-title-large">
            {sectionRequest.isPending && (
              <>
                <ClockIcon className="stroke-fixed-accent-color" size="20" />
                {t('lti.configuration.invoice_request')}:{' '}
                {t('lti.configuration.invoice_request_status_pending')}
              </>
            )}
            {sectionRequest.isRejected && (
              <>
                <CircleX
                  className="rounded-full bg-core-error stroke-core-on-error"
                  size="20"
                />
                {t('lti.configuration.invoice_request')}:{' '}
                {t('lti.configuration.invoice_request_status_rejected')}
              </>
            )}
          </div>
          <div className="rounded-xl bg-core-tertiary p-4">
            <table role="presentation" className="w-full">
              <tbody>
                <tr>
                  <td className="w-[50%] text-body-medium text-on-surface-var">
                    {t('lti.configuration.invoice_request_submitted_on')}
                  </td>
                  <td className="text-label-medium">
                    {sectionRequest.data.requestedAt.toDateString()}
                  </td>
                </tr>
                <tr>
                  <td className="w-[50%] text-body-medium text-on-surface-var">
                    {t('lti.configuration.invoice_request_class_name')}
                  </td>
                  <td className="text-label-medium">
                    {sectionCubit.section.data.className}
                  </td>
                </tr>
                <tr>
                  <td className="w-[50%] text-body-medium text-on-surface-var">
                    {t('lti.configuration.invoice_request_section_name')}
                  </td>
                  <td className="text-label-medium">
                    {sectionCubit.section.data.sectionName}
                  </td>
                </tr>
                <tr>
                  <td className="text-body-medium text-on-surface-var">
                    {t('lti.configuration.invoice_request_number_of_students')}
                  </td>
                  <td className="text-label-medium">
                    {sectionRequest.data.sectionRequestStudentCount}
                  </td>
                </tr>
                {/* branch on the pricing model the estimate actually used, not
                    on whether a SectionProduct doc exists — a product whose
                    price is not configured falls back to token pricing, and
                    would otherwise render an empty Pricing row */}
                {isProductPriced && (
                  <>
                    <tr>
                      <td className="text-body-medium text-on-surface-var">
                        {tt.lti.configuration.pricing()}
                      </td>
                      <td className="text-label-medium">
                        {productName}
                        {' - '}
                        {tt.lti.configuration.invoice_request_cost_per_participant(
                          {
                            cost: formatCurrency(
                              productPriceCents / 100,
                              'USD',
                              {
                                maximumFractionDigits:
                                  productPriceCents % 100 === 0 ? 0 : 2,
                              }
                            ),
                          }
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-body-medium text-on-surface-var">
                        {tt.lti.configuration.invoice_request_estimated_cost()}
                      </td>
                      <td className="text-label-medium">{estimatedCost}</td>
                    </tr>
                  </>
                )}
                {!isProductPriced && (
                  <>
                    <tr>
                      <td className="text-body-medium text-on-surface-var">
                        {t(
                          'lti.configuration.invoice_request_number_of_experiences'
                        )}
                      </td>
                      <td className="text-label-medium">
                        {sectionRequest.data.sectionRequestAssignmentCount}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-body-medium text-on-surface-var">
                        {t('lti.configuration.invoice_request_estimated_cost')}
                      </td>
                      <td className="text-label-medium">{estimatedCost}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
          {sectionRequest.isRejected && (
            <div className="mt-2 rounded-xl bg-core-tertiary p-4">
              <table role="presentation" className="w-full">
                <tbody>
                  <tr>
                    <td className="w-[50%] text-body-medium text-on-surface-var">
                      {t('lti.configuration.invoice_request_rejected_by')}
                    </td>
                    <td className="text-label-medium">
                      {rejectedBy
                        ? rejectedBy.fullName
                        : tt.lti.configuration.admin()}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-body-medium text-on-surface-var">
                      {t('lti.configuration.invoice_request_rejected_datetime')}
                    </td>
                    <td className="text-label-medium">
                      {sectionRequest.data.requestedAt.toDateString()}
                    </td>
                  </tr>
                  <tr>
                    <td
                      className="text-body-medium text-on-surface-var"
                      colSpan={2}
                    >
                      {t('lti.configuration.invoice_request_rejected_reason')}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-label-medium" colSpan={2}>
                      <div className="py-2">
                        {sectionRequest.data.sectionRequestReason}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4 flex flex-row justify-between">
            <BreakoutButton
              onClick={() => {
                showDialog(() => {
                  return (
                    <ConfirmationDialog
                      title={tt.lti.configuration.change_to_student_pay_confirmation()}
                      titleClassName="text-headline-large"
                      subtitle={tt.lti.configuration.change_to_student_pay_confirmation_subtitle()}
                      buttonText={tt.lti.configuration.withdraw_request()}
                      onConfirm={setToStudentPay}
                    />
                  )
                })
              }}
              kind="tertiary"
              type="submit"
              size="small"
            >
              {tt.lti.configuration.change_to_student_pay()}
            </BreakoutButton>
            {sectionRequest.isRejected && (
              <BreakoutButton
                onClick={createNewRequest}
                kind="accent"
                type="submit"
                size="small"
              >
                {tt.lti.configuration.create_new_request()}
              </BreakoutButton>
            )}
          </div>
        </div>
      </div>
    </div>
  )
})
